/**
 * THE NATIVE SEAM.
 *
 * One rule, and it is the rule that lets one codebase be an iPhone app and a website:
 * **nothing outside this folder may import a Capacitor plugin.** Features import from
 * here; each module below exports one interface with a native path and a web path, picked
 * once at load.
 *
 * Three things fall out of it. The web build never ships a control that cannot work. The
 * test suite runs with no device and no plugin mocks scattered through features. Adding
 * Android later touches this folder and nothing else.
 *
 * eslint enforces it (see eslint.config.js → no-restricted-imports).
 *
 * THE SECOND RULE, AND IT IS WHAT THIS FILE IS FOR: **every module in this folder is
 * exported here, and this file is the only way in.** `@/app/platform/<module>` is not a
 * second door. A module missing from the list below does not read as an oversight, it reads
 * as a deliberate exception someone has to reconstruct — three separate agents in three
 * separate waves each stopped to work out whether `browser` was one (MOB-17). It was not.
 * One module left off the list costs more reading time than the whole list does.
 *
 * The one real objection to exporting everything is that it drags every plugin into the
 * import graph of anything that touches this file. Measured on this project rather than
 * assumed: vite gives `@capacitor/*` its own `native-vendor` chunk (vite.config.ts →
 * manualChunks), and that chunk is a STATIC import of the entry chunk, preloaded in
 * index.html — so every plugin named below is on the launch path whatever this barrel says.
 * Building the app with `browser` off the list and its call sites deep-importing produced a
 * byte-identical `native-vendor` (same content hash, 25.48 kB) and the same entry chunk
 * size. Exporting it costs nothing; NOT exporting it is paid for by every reader.
 *
 * Where that objection would really bite is a plugin heavy enough to deserve a dynamic
 * import. The answer then is a lazy path inside that module — never a hole in this list.
 *
 * `src/tests/architecture/structure.test.ts` holds both halves: every module exported, and
 * nothing outside the folder importing one directly.
 */
import { Capacitor } from "@capacitor/core";

export const isNative = Capacitor.isNativePlatform();
export const platform = Capacitor.getPlatform() as "ios" | "android" | "web";
export const isIOS = platform === "ios";

export * from "./storage";
export * from "./preferences";
export * from "./haptics";
export * from "./heart-rate";
export * from "./keep-awake";
export * from "./status-bar";
export * from "./splash";
export * from "./keyboard";
export * from "./network";
export * from "./push";
export * from "./links";
export * from "./camera";
export * from "./browser";
export * from "./app-state";
export * from "./settings";
export * from "./updates";
export * from "./reporting";
